import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Crown, Lock, Gem, Star } from "lucide-react";
import { useEffect, useState } from "react";

const Hero = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-background">
      {/* Enhanced gradient orbs with more color */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 -left-32 w-[700px] h-[700px] bg-gradient-to-br from-primary/20 via-primary/10 to-secondary/5 rounded-full blur-[150px] animate-pulse" />
        <div className="absolute bottom-1/4 -right-32 w-[600px] h-[600px] bg-gradient-to-tl from-accent/20 via-tertiary/10 to-secondary/5 rounded-full blur-[140px] animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] bg-gradient-to-r from-secondary/10 via-tertiary/5 to-accent/10 rounded-full blur-[200px]" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[500px] bg-gradient-to-b from-primary/10 via-secondary/5 to-transparent" />
      </div>

      {/* Premium animated grid pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px] [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_80%)]" />

      {/* Content */}
      <div className="container mx-auto px-4 md:px-8 lg:px-16 py-24 md:py-32 relative z-10">
        <div className="max-w-5xl mx-auto text-center">
          
          {/* Exclusive badge with gradient border */}
          <div 
            className={`mb-8 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
          >
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-primary/10 via-secondary/10 to-accent/10 border border-primary/40 backdrop-blur-sm shadow-[0_0_30px_rgba(217,70,239,0.2)]">
              <Lock className="w-4 h-4 text-primary animate-pulse" />
              <span className="text-sm font-semibold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent uppercase tracking-wider">Invite-Only Network</span>
              <Crown className="w-4 h-4 text-accent animate-pulse" />
            </div>
          </div>

          {/* Main Headline */}
          <h1 
            className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold leading-[1.1] tracking-tight mb-4 sm:mb-6 transition-all duration-1000 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
          >
            <span className="text-foreground">Unlock Elite Status—</span>
            <br className="hidden sm:block" />
            <span className="sm:hidden"> </span>
            <span className="bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent animate-gradient-shift bg-[length:200%_auto]">
              Creators Wanted, Not Needed
            </span>
          </h1>

          {/* Subheadline */}
          <p 
            className={`text-base sm:text-lg md:text-xl lg:text-2xl text-muted-foreground max-w-3xl mx-auto mb-3 sm:mb-4 px-2 leading-relaxed transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
          >
            The most exclusive TikTok Live creator network. Keep <span className="text-foreground font-semibold">100% of your earnings</span>. 
            No fees. No contracts. No exceptions.
          </p>

          {/* FOMO Text */}
          <p 
            className={`text-xs sm:text-sm md:text-base text-primary/80 font-medium mb-8 sm:mb-10 px-2 transition-all duration-1000 delay-250 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
          >
            ⚡ Only accepting 15 new creators this month. Applications close when spots fill.
          </p>

          {/* CTA Buttons */}
          <div 
            className={`flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center mb-12 sm:mb-16 px-4 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
          >
            <Link to="/apply" className="w-full sm:w-auto">
              <Button 
                variant="premium" 
                size="lg" 
                className="group w-full sm:w-auto text-sm sm:text-base md:text-lg px-6 sm:px-10 md:px-12 py-5 sm:py-6 md:py-7 font-bold transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_0_60px_rgba(217,70,239,0.5)] animate-pulse-glow"
              >
                <Gem className="mr-2 w-4 h-4 sm:w-5 sm:h-5" />
                Apply for Exclusive Access
                <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/program" className="w-full sm:w-auto">
              <Button 
                variant="ghost" 
                size="lg" 
                className="w-full sm:w-auto text-sm sm:text-base md:text-lg px-6 sm:px-8 md:px-10 py-4 sm:py-5 md:py-6 font-medium text-muted-foreground hover:text-foreground border border-border/50 hover:border-primary/30"
              >
                See What You're Missing
              </Button>
            </Link>
          </div>

          {/* Elite Status Pills with gradient backgrounds */}
          <div 
            className={`grid grid-cols-2 sm:flex sm:flex-wrap justify-center gap-2 sm:gap-3 md:gap-4 mb-12 sm:mb-16 md:mb-20 px-2 transition-all duration-1000 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
          >
            {[
              { icon: Crown, text: "Invite-Only Access", gradient: "from-primary/20 to-secondary/20" },
              { icon: Gem, text: "0% Commission Forever", gradient: "from-secondary/20 to-tertiary/20" },
              { icon: Lock, text: "Zero Contracts", gradient: "from-tertiary/20 to-accent/20" },
              { icon: Star, text: "Elite Support 24/7", gradient: "from-accent/20 to-primary/20" },
            ].map((item, index) => (
              <div 
                key={index}
                className={`flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-full border border-border/30 bg-gradient-to-r ${item.gradient} backdrop-blur-sm hover:border-primary/50 hover:shadow-[0_0_20px_rgba(217,70,239,0.2)] transition-all duration-300`}
              >
                <item.icon className="w-3 h-3 sm:w-4 sm:h-4 text-primary flex-shrink-0" />
                <span className="text-xs sm:text-sm font-medium text-foreground/90 whitespace-nowrap">{item.text}</span>
              </div>
            ))}
          </div>

          {/* Stats Grid with gradient backgrounds */}
          <div 
            className={`grid grid-cols-2 md:grid-cols-4 gap-px bg-gradient-to-r from-primary/30 via-secondary/30 to-accent/30 rounded-xl sm:rounded-2xl overflow-hidden max-w-4xl mx-auto transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
          >
            {[
              { value: "$10M+", label: "Paid to Creators", sublabel: "in 2024", gradient: "from-primary/5 to-secondary/5" },
              { value: "<5%", label: "Acceptance Rate", sublabel: "highly selective", gradient: "from-secondary/5 to-tertiary/5" },
              { value: "100+", label: "Elite Creators", sublabel: "in network", gradient: "from-tertiary/5 to-accent/5" },
              { value: "0%", label: "Platform Fees", sublabel: "forever", gradient: "from-accent/5 to-primary/5" },
            ].map((stat, index) => (
              <div 
                key={index}
                className={`bg-gradient-to-br ${stat.gradient} bg-card/60 backdrop-blur-sm p-4 sm:p-6 md:p-8 text-center hover:bg-card/80 transition-all duration-300 group relative overflow-hidden`}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="relative z-10">
                  <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent mb-0.5 sm:mb-1 group-hover:scale-105 transition-transform animate-gradient-shift bg-[length:200%_auto]">
                    {stat.value}
                  </div>
                  <div className="text-xs sm:text-sm font-medium text-foreground/80">{stat.label}</div>
                  <div className="text-[10px] sm:text-xs text-muted-foreground">{stat.sublabel}</div>
                </div>
              </div>
            ))}
          </div>

        </div>
      </div>

      {/* Bottom fade with gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-background via-background/80 to-transparent pointer-events-none" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
    </section>
  );
};

export default Hero;
